import { useCallback, useEffect, useRef, useState } from 'react';
import type { Bbox, ImageTransform } from '../useImageTransform';

/**
 * Touch-first pointer state machine for dragging + resizing a
 * bounding box on the annotation stage.
 *
 * Works on mouse, stylus, AND touch via the Pointer Events API —
 * one code path, no `touchstart`/`mousedown` fork. React Native
 * Web passes `onPointerDown/Move/Up/Cancel` through verbatim on
 * `<div>`, which is what the SVG `<rect>` we render effectively
 * is.
 *
 * Contract:
 *   - caller seeds the hook with an initial imageSpace bbox + the
 *     `transform` hook (for clamping against the image's actual
 *     rendered bounds)
 *   - caller renders 8 resize handles + a drag body; each one
 *     calls `startDrag(kind)` from its `onPointerDown` handler
 *   - during a drag the hook updates its `box` state live; the
 *     caller renders that box as the overlay
 *   - caller reads `box` on Apply → commits to the server
 *   - caller calls `reset(newBox)` to jump to a fresh initial bbox
 *     (e.g. when user re-enters refine mode for a different item)
 *
 * Clamps during drag (not on commit): bbox must stay within
 * [0, imageWidth] × [0, imageHeight] and can't shrink below
 * MIN_EDGE_PX on either side. Cancelling a drag (pointercancel or
 * Escape) reverts to the pre-drag box.
 */

export type DragKind =
  | 'move'
  | 'resize-nw'
  | 'resize-n'
  | 'resize-ne'
  | 'resize-e'
  | 'resize-se'
  | 'resize-s'
  | 'resize-sw'
  | 'resize-w';

export const MIN_EDGE_PX = 16;

export interface UseBoxDrawResult {
  /** Current image-space bbox — caller renders this as the overlay. */
  box: Bbox;
  /** True while any drag is mid-gesture. Prevents double-arm and
   *  lets the caller freeze other interactions. */
  isDragging: boolean;
  /** Call from a handle's `onPointerDown` with the corresponding
   *  kind. The dom event is captured automatically. */
  startDrag: (kind: DragKind, event: PointerEventLike) => void;
  /** True if the current box differs from the initial seed — useful
   *  for enabling/disabling Apply. */
  dirty: boolean;
  /** Jump to a new initial bbox (e.g. re-enter refine for a
   *  different item). Cancels any in-flight drag. */
  reset: (nextBox: Bbox) => void;
  /** Force-revert to the initial seed and end any drag. Exposed for
   *  Cancel button / Escape key handlers. */
  cancel: () => void;
}

/** Shape-compatible with both `React.PointerEvent<T>` and
 *  `PointerEvent` — so we can accept the native event from
 *  `setPointerCapture` handoffs. */
export interface PointerEventLike {
  clientX: number;
  clientY: number;
  pointerId: number;
  /** Not always present on synthetic events; optional. */
  currentTarget?: { setPointerCapture?: (id: number) => void } | null;
}

/** Bounds the bbox can move inside (imageSpace). */
interface Bounds {
  w: number;
  h: number;
}

interface DragState {
  kind: DragKind;
  /** imageSpace starting bbox when the drag opened. */
  startBox: Bbox;
  /** imageSpace pointer position at drag start. */
  startImagePoint: { x: number; y: number };
  pointerId: number;
}

export function useBoxDraw(
  initial: Bbox,
  transform: ImageTransform,
  containerRef: React.RefObject<HTMLElement | null>,
): UseBoxDrawResult {
  const [box, setBox] = useState<Bbox>(initial);
  const initialRef = useRef(initial);
  const dragRef = useRef<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Whenever the caller seeds a new initial (e.g. refine for a new
  // item), overwrite both the state and our baseline snapshot.
  const reset = useCallback((nextBox: Bbox) => {
    initialRef.current = nextBox;
    setBox(nextBox);
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const cancel = useCallback(() => {
    setBox(initialRef.current);
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  // Derive bounds from the latest imageSize snapshot. We deliberately
  // re-read from the transform on every update rather than caching —
  // if the user resizes the window mid-drag the bounds shift but we
  // want clamping to track the new rect live.
  const getBounds = useCallback((): Bounds | null => {
    if (!transform.imageSize) return null;
    return { w: transform.imageSize.width, h: transform.imageSize.height };
  }, [transform]);

  // Convert a pointer's clientX/Y into imageSpace via the transform.
  const pointerToImage = useCallback(
    (evt: PointerEventLike): { x: number; y: number } | null => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const dom = {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top,
      };
      return transform.toImage(dom);
    },
    [containerRef, transform],
  );

  const startDrag = useCallback(
    (kind: DragKind, event: PointerEventLike) => {
      const img = pointerToImage(event);
      if (!img) return;
      dragRef.current = {
        kind,
        startBox: box,
        startImagePoint: img,
        pointerId: event.pointerId,
      };
      // Capture pointer so the drag follows the cursor even when it
      // leaves the handle element (critical for small touch targets).
      try {
        event.currentTarget?.setPointerCapture?.(event.pointerId);
      } catch {
        /* noop — some envs don't expose setPointerCapture */
      }
      setIsDragging(true);
    },
    [box, pointerToImage],
  );

  // Document-level pointer listeners so the drag keeps tracking
  // after the pointer leaves the original handle. Pointer capture
  // handles this for most browsers, but the doc-level fallback is
  // belt + suspenders for touch cancel edge cases.
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const img = pointerToImage({
        clientX: e.clientX,
        clientY: e.clientY,
        pointerId: e.pointerId,
      });
      if (!img) return;
      const bounds = getBounds();
      if (!bounds) return;

      const dx = img.x - drag.startImagePoint.x;
      const dy = img.y - drag.startImagePoint.y;
      setBox(applyDrag(drag.kind, drag.startBox, dx, dy, bounds));
    };

    const handleUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      dragRef.current = null;
      setIsDragging(false);
    };

    const handleCancel = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      // Revert to starting box on cancel so the user isn't left
      // with a half-resolved drag when they hit Escape or the
      // browser decides to snatch pointer control.
      setBox(drag.startBox);
      dragRef.current = null;
      setIsDragging(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel(new PointerEvent('pointercancel', { pointerId: -1 }));
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleCancel);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleCancel);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isDragging, pointerToImage, getBounds]);

  const dirty = !bboxesEqual(box, initialRef.current);

  return { box, isDragging, startDrag, dirty, reset, cancel };
}

/** Apply a drag delta to a starting bbox, returning a clamped
 *  imageSpace bbox. Exported for unit tests. */
export function applyDrag(
  kind: DragKind,
  start: Bbox,
  dx: number,
  dy: number,
  bounds: Bounds,
): Bbox {
  switch (kind) {
    case 'move': {
      const nx = clamp(start.x + dx, 0, bounds.w - start.w);
      const ny = clamp(start.y + dy, 0, bounds.h - start.h);
      return { x: nx, y: ny, w: start.w, h: start.h };
    }
    case 'resize-nw':
      return resizeClamped(start, bounds, {
        leftDx: dx,
        topDy: dy,
      });
    case 'resize-n':
      return resizeClamped(start, bounds, { topDy: dy });
    case 'resize-ne':
      return resizeClamped(start, bounds, {
        rightDx: dx,
        topDy: dy,
      });
    case 'resize-e':
      return resizeClamped(start, bounds, { rightDx: dx });
    case 'resize-se':
      return resizeClamped(start, bounds, {
        rightDx: dx,
        bottomDy: dy,
      });
    case 'resize-s':
      return resizeClamped(start, bounds, { bottomDy: dy });
    case 'resize-sw':
      return resizeClamped(start, bounds, {
        leftDx: dx,
        bottomDy: dy,
      });
    case 'resize-w':
      return resizeClamped(start, bounds, { leftDx: dx });
    default:
      return start;
  }
}

interface ResizeDeltas {
  leftDx?: number;
  rightDx?: number;
  topDy?: number;
  bottomDy?: number;
}

const resizeClamped = (
  start: Bbox,
  bounds: Bounds,
  d: ResizeDeltas,
): Bbox => {
  let x = start.x;
  let y = start.y;
  let w = start.w;
  let h = start.h;
  if (typeof d.leftDx === 'number') {
    // Moving the left edge: x increases, w decreases. Clamp min w.
    const newX = clamp(start.x + d.leftDx, 0, start.x + start.w - MIN_EDGE_PX);
    w = start.w - (newX - start.x);
    x = newX;
  }
  if (typeof d.rightDx === 'number') {
    const newW = clamp(start.w + d.rightDx, MIN_EDGE_PX, bounds.w - start.x);
    w = newW;
  }
  if (typeof d.topDy === 'number') {
    const newY = clamp(start.y + d.topDy, 0, start.y + start.h - MIN_EDGE_PX);
    h = start.h - (newY - start.y);
    y = newY;
  }
  if (typeof d.bottomDy === 'number') {
    const newH = clamp(start.h + d.bottomDy, MIN_EDGE_PX, bounds.h - start.y);
    h = newH;
  }
  return { x, y, w, h };
};

const clamp = (n: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, n));

const bboxesEqual = (a: Bbox, b: Bbox): boolean =>
  Math.abs(a.x - b.x) < 0.5 &&
  Math.abs(a.y - b.y) < 0.5 &&
  Math.abs(a.w - b.w) < 0.5 &&
  Math.abs(a.h - b.h) < 0.5;
